import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";

import { CopyButton } from "~/components/copy-button";
import { DetailField, FieldGrid } from "~/components/detail-field";
import { JsonViewer } from "~/components/json-viewer";
import { PageHeader } from "~/components/page-header";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { errorMessage, isNotFound } from "~/lib/api";
import { formatDateTime, formatIDR, orDash } from "~/lib/format";
import { useDisbursement, type DisbursementDetail } from "~/lib/queries";

export const Route = createFileRoute("/_authed/disbursements/$disbursementId")({
  component: DisbursementDetailPage,
});

/** One payout, and the parameters it was created with. */
function DisbursementDetailPage() {
  const { disbursementId } = Route.useParams();
  const { data, error, isPending, refetch } = useDisbursement(disbursementId);

  if (isPending) return <DetailSkeleton />;

  if (isNotFound(error)) {
    return <EmptyState body="Nothing in disbursements has this id." />;
  }

  if (error || !data) {
    return <EmptyState body={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" render={<Link to="/disbursements" />}>
          <ArrowLeftIcon className="size-3.5" />
          All disbursements
        </Button>
      </div>

      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <span className="tabular-nums">{formatIDR(data.amount)}</span>
            <CopyButton value={data.disbursement_id} label="disbursement id" />
          </span>
        }
        description={
          <span className="text-muted-foreground">
            {orDash(data.disbursement_method)} · {formatDateTime(data.created_at)}
          </span>
        }
      />

      <Overview detail={data} />
      <Params detail={data} />
    </div>
  );
}

function Overview({ detail }: { detail: DisbursementDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Payout</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGrid>
          <DetailField label="Disbursement ID" copy={detail.disbursement_id} mono>
            {detail.disbursement_id}
          </DetailField>
          <DetailField label="Amount">
            <span className="tabular-nums">{formatIDR(detail.amount)}</span>
          </DetailField>
          <DetailField label="Method">{orDash(detail.disbursement_method)}</DetailField>
          <DetailField
            label="Destination"
            copy={detail.destination ?? undefined}
            mono={Boolean(detail.destination)}
          >
            {orDash(detail.destination)}
          </DetailField>
          <DetailField label="Created">{formatDateTime(detail.created_at)}</DetailField>
          <DetailField label="Updated">{formatDateTime(detail.updated_at)}</DetailField>
        </FieldGrid>
      </CardContent>
    </Card>
  );
}

/**
 * The params blob, whole.
 *
 * Unstructured by design — it holds whatever the caller passed for the payout
 * method in question — so it is shown as it is rather than projected into named
 * fields that would be right for one method and wrong for the next.
 */
function Params({ detail }: { detail: DisbursementDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Params</CardTitle>
        <CardDescription>
          The jsonb column, as stored. Its shape depends on the payout method.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <JsonViewer value={detail.params ?? null} />
      </CardContent>
    </Card>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-44 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

function EmptyState({ body, onRetry }: { body: React.ReactNode; onRetry?: () => void }) {
  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Couldn't open this disbursement</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          {onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" render={<Link to="/disbursements" />}>
            Back to disbursements
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
